import React, { useEffect, useState } from 'react';
import { NavLink, useLocation } from 'react-router-dom';
import Box from '@mui/material/Box';
import Drawer from '@mui/material/Drawer';
import List from '@mui/material/List';
import ListItemButton from '@mui/material/ListItemButton';
import ListItemIcon from '@mui/material/ListItemIcon';
import ListItemText from '@mui/material/ListItemText';
import Typography from '@mui/material/Typography';
import Divider from '@mui/material/Divider';
import Badge from '@mui/material/Badge';
import useMediaQuery from '@mui/material/useMediaQuery';
import { useTheme } from '@mui/material/styles';
import DashboardIcon from '@mui/icons-material/Dashboard';
import WorkOutlineIcon from '@mui/icons-material/WorkOutline';
import EventIcon from '@mui/icons-material/Event';
import CalendarMonthIcon from '@mui/icons-material/CalendarMonth';
import AttachMoneyIcon from '@mui/icons-material/AttachMoney';
import ShareIcon from '@mui/icons-material/Share';
import PersonIcon from '@mui/icons-material/Person';
import QuizIcon from '@mui/icons-material/Quiz';
import AccountBalanceWalletIcon from '@mui/icons-material/AccountBalanceWallet';
import { SIDEBAR_WIDTH } from '../../utils/constants';
import { getStripeStatus } from '../../services/internApi';

const navItems = [
  { label: 'Dashboard', path: '/', icon: <DashboardIcon /> },
  { label: 'Onboarding Test', path: '/onboarding-test', icon: <QuizIcon /> },
  { label: 'Available Jobs', path: '/jobs', icon: <WorkOutlineIcon /> },
  { label: 'My Bookings', path: '/bookings', icon: <EventIcon /> },
  { label: 'Availability', path: '/availability', icon: <CalendarMonthIcon /> },
  { label: 'Earnings', path: '/earnings', icon: <AttachMoneyIcon /> },
  { label: 'Referrals', path: '/referrals', icon: <ShareIcon /> },
  { label: 'Payment Setup', path: '/payouts/setup', icon: <AccountBalanceWalletIcon />, key: 'stripe' },
  { label: 'Profile', path: '/profile', icon: <PersonIcon /> },
];

export default function Sidebar({ mobileOpen, onMobileClose }) {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));
  const location = useLocation();
  const [stripeNeedsSetup, setStripeNeedsSetup] = useState(false);

  // Lightweight one-shot fetch — the dashboard already calls this on its own,
  // but we need it here to drive the badge regardless of which page is active.
  useEffect(() => {
    let cancelled = false;
    getStripeStatus()
      .then((data) => {
        if (cancelled) return;
        setStripeNeedsSetup(!data?.stripe_payouts_enabled);
      })
      .catch(() => {
        // Silent — sidebar shouldn't break on stripe status errors.
      });
    return () => { cancelled = true; };
  }, []);

  const drawerContent = (
    <Box sx={{ height: '100%', display: 'flex', flexDirection: 'column' }}>
      <Box
        sx={{
          p: 2.5,
          display: 'flex',
          alignItems: 'center',
          gap: 1.5,
        }}
      >
        <Box component="img" src="/images/cwc-logo-full.png" alt="CWC"
          sx={{ height: 48, objectFit: 'contain', borderRadius: 0.5 }} />
        <Box>
          <Typography variant="caption" sx={{ color: 'text.secondary' }}>
            Intern Portal
          </Typography>
        </Box>
      </Box>
      <Divider />
      <List sx={{ flex: 1, px: 1.5, py: 1 }}>
        {navItems.map((item) => {
          const isActive =
            item.path === '/'
              ? location.pathname === '/'
              : location.pathname.startsWith(item.path);

          return (
            <ListItemButton
              key={item.path}
              component={NavLink}
              to={item.path}
              onClick={isMobile ? onMobileClose : undefined}
              sx={{
                borderRadius: 1.5,
                mb: 0.5,
                backgroundColor: isActive ? 'primary.main' : 'transparent',
                color: isActive ? 'white' : 'text.primary',
                '&:hover': {
                  backgroundColor: isActive ? 'primary.dark' : 'action.hover',
                },
              }}
            >
              <ListItemIcon
                sx={{
                  color: isActive ? 'white' : 'text.secondary',
                  minWidth: 40,
                }}
              >
                {item.key === 'stripe' && stripeNeedsSetup ? (
                  <Badge color="warning" variant="dot" overlap="circular">
                    {item.icon}
                  </Badge>
                ) : (
                  item.icon
                )}
              </ListItemIcon>
              <ListItemText
                primary={item.label}
                primaryTypographyProps={{ fontWeight: isActive ? 600 : 400, fontSize: '0.9rem' }}
              />
            </ListItemButton>
          );
        })}
      </List>
    </Box>
  );

  if (isMobile) {
    return (
      <Drawer
        variant="temporary"
        open={mobileOpen}
        onClose={onMobileClose}
        ModalProps={{ keepMounted: true }}
        sx={{
          '& .MuiDrawer-paper': {
            width: SIDEBAR_WIDTH,
            boxSizing: 'border-box',
          },
        }}
      >
        {drawerContent}
      </Drawer>
    );
  }

  return (
    <Drawer
      variant="permanent"
      sx={{
        width: SIDEBAR_WIDTH,
        flexShrink: 0,
        '& .MuiDrawer-paper': {
          width: SIDEBAR_WIDTH,
          boxSizing: 'border-box',
          borderRight: '1px solid',
          borderColor: 'divider',
        },
      }}
    >
      {drawerContent}
    </Drawer>
  );
}
